import { useState, useMemo, useCallback } from 'react';
import {
  ChevronDown,
  ChevronRight,
  Building2,
  Layers,
  Cpu,
  TreePine,
  Search,
  Copy,
  Check,
} from 'lucide-react';
import type { ProductKnowledgeNode } from '../services/product-knowledge-tree';
import { treeToLLMContext } from '../services/product-knowledge-tree';

// ---------------------------------------------------------------------------
// 样式常量
// ---------------------------------------------------------------------------
const levelColors: Record<string, { bg: string; text: string; icon: string }> = {
  root: { bg: 'rgba(0,113,227,0.06)', text: 'var(--color-key)', icon: 'var(--color-key)' },
  competitor: { bg: 'rgba(139,92,246,0.08)', text: '#7c3aed', icon: '#8b5cf6' },
  series: { bg: 'rgba(16,185,129,0.08)', text: '#059669', icon: '#10b981' },
  product: { bg: 'rgba(245,158,11,0.08)', text: '#b45309', icon: '#f59e0b' },
};

const LevelIcon: Record<string, typeof Building2> = {
  root: TreePine,
  competitor: Building2,
  series: Layers,
  product: Cpu,
};

// ---------------------------------------------------------------------------
// 主组件
// ---------------------------------------------------------------------------
interface KnowledgeTreeViewerProps {
  tree: ProductKnowledgeNode;
  onSelectProduct?: (spec: NonNullable<ProductKnowledgeNode['product_spec']>) => void;
}

export function KnowledgeTreeViewer({ tree, onSelectProduct }: KnowledgeTreeViewerProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [copied, setCopied] = useState(false);

  const handleCopyContext = useCallback(() => {
    const context = treeToLLMContext(tree);
    navigator.clipboard.writeText(context).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [tree]);

  return (
    <div
      className="rounded-xl border overflow-hidden"
      style={{
        borderColor: 'var(--color-system-quaternary)',
        backgroundColor: 'var(--color-background-primary)',
      }}
    >
      {/* 头部 */}
      <div
        className="flex items-center justify-between border-b px-4 py-3"
        style={{ borderColor: 'var(--color-system-quaternary)' }}
      >
        <div className="flex items-center gap-2">
          <TreePine size={16} style={{ color: 'var(--color-key)' }} />
          <span className="text-sm font-semibold" style={{ color: 'var(--color-system-primary)' }}>
            产品知识树
          </span>
          <span
            className="rounded-full px-2 py-0.5 text-[10px] font-medium"
            style={{ backgroundColor: 'rgba(0,113,227,0.08)', color: 'var(--color-key)' }}
          >
            PageIndex 风格
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleCopyContext}
            className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] font-medium transition hover:opacity-80"
            style={{ borderColor: 'var(--color-system-quaternary)', color: 'var(--color-system-secondary)' }}
            title="复制知识树为文本（可直接发给 LLM）"
          >
            {copied ? <Check size={10} /> : <Copy size={10} />}
            {copied ? '已复制' : '导出 LLM Context'}
          </button>
        </div>
      </div>

      {/* 搜索 */}
      <div className="border-b px-4 py-2" style={{ borderColor: 'var(--color-system-quaternary)' }}>
        <div className="relative">
          <Search
            size={12}
            className="absolute left-2.5 top-1/2 -translate-y-1/2"
            style={{ color: 'var(--color-system-tertiary)' }}
          />
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="搜索产品、系列或竞品..."
            className="w-full rounded-md border py-1.5 pl-7 pr-3 text-xs outline-none transition focus:border-[var(--color-key)]"
            style={{
              borderColor: 'var(--color-system-quaternary)',
              backgroundColor: 'var(--color-background-secondary)',
            }}
          />
        </div>
      </div>

      {/* 树内容 */}
      <div className="max-h-[480px] overflow-y-auto px-2 py-2">
        <TreeNode
          node={tree}
          depth={0}
          searchQuery={searchQuery}
          onSelectProduct={onSelectProduct}
          defaultExpanded
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 递归树节点
// ---------------------------------------------------------------------------
function TreeNode({
  node,
  depth,
  searchQuery,
  onSelectProduct,
  defaultExpanded = false,
}: {
  node: ProductKnowledgeNode;
  depth: number;
  searchQuery: string;
  onSelectProduct?: (spec: NonNullable<ProductKnowledgeNode['product_spec']>) => void;
  defaultExpanded?: boolean;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded || depth < 1);
  const hasChildren = node.children.length > 0;
  const colors = levelColors[node.level] || levelColors.root;
  const Icon = LevelIcon[node.level] || Cpu;

  // 搜索过滤
  const matchesSearch = useMemo(() => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return nodeMatchesQuery(node, q);
  }, [node, searchQuery]);

  // 自动展开匹配的节点
  const forceExpanded = useMemo(() => {
    if (!searchQuery.trim()) return false;
    return hasChildren && node.children.some((c) => nodeMatchesQuery(c, searchQuery.toLowerCase()));
  }, [node, searchQuery, hasChildren]);

  const isExpanded = expanded || forceExpanded;

  if (!matchesSearch && !forceExpanded) return null;

  const isProduct = node.level === 'product';

  return (
    <div>
      <div
        className="group flex items-start gap-1.5 rounded-lg px-2 py-1.5 transition"
        style={{
          paddingLeft: `${depth * 16 + 8}px`,
          cursor: hasChildren || isProduct ? 'pointer' : 'default',
        }}
        onClick={() => {
          if (hasChildren) setExpanded(!isExpanded);
          if (isProduct && node.product_spec && onSelectProduct) {
            onSelectProduct(node.product_spec);
          }
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLDivElement).style.backgroundColor = 'var(--color-background-secondary)';
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLDivElement).style.backgroundColor = 'transparent';
        }}
      >
        {/* 展开箭头 */}
        <span className="mt-0.5 shrink-0" style={{ width: 14, color: 'var(--color-system-tertiary)' }}>
          {hasChildren ? (
            isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />
          ) : (
            <span style={{ display: 'inline-block', width: 14 }} />
          )}
        </span>

        {/* 图标 */}
        <span
          className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded"
          style={{ backgroundColor: colors.bg, color: colors.icon }}
        >
          <Icon size={11} />
        </span>

        {/* 内容 */}
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-1.5">
            <span className="text-xs font-semibold" style={{ color: colors.text }}>
              {node.title}
            </span>
            {hasChildren && (
              <span className="text-[10px]" style={{ color: 'var(--color-system-tertiary)' }}>
                ({node.children.length})
              </span>
            )}
          </div>
          <div
            className="mt-0.5 text-[11px] leading-4"
            style={{ color: 'var(--color-system-secondary)' }}
          >
            {node.summary}
          </div>

          {/* 产品节点的元数据标签 */}
          {isProduct && (
            <div className="mt-1 flex flex-wrap gap-1">
              {Object.entries(node.metadata)
                .filter(([, v]) => v != null)
                .map(([key, value]) => (
                  <span
                    key={key}
                    className="rounded-full px-1.5 py-0.5 text-[9px] font-medium"
                    style={{
                      backgroundColor: 'var(--color-background-secondary)',
                      color: 'var(--color-system-tertiary)',
                    }}
                  >
                    {key}: {value}
                  </span>
                ))}
            </div>
          )}
        </div>
      </div>

      {/* 子节点 */}
      {isExpanded && hasChildren && (
        <div>
          {node.children.map((child) => (
            <TreeNode
              key={child.node_id}
              node={child}
              depth={depth + 1}
              searchQuery={searchQuery}
              onSelectProduct={onSelectProduct}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 搜索辅助
// ---------------------------------------------------------------------------
function nodeMatchesQuery(node: ProductKnowledgeNode, query: string): boolean {
  const text = [node.title, node.summary, ...Object.values(node.metadata)]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  if (text.includes(query)) return true;
  return node.children.some((c) => nodeMatchesQuery(c, query));
}
